import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { QuebecObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { QuebecObstetricalFilePage, QuebecObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Quebec/QuebecObstetricalFilePage";
import { QuebecObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Quebec/QuebecObstetricalFileFieldLimits";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { EstimatedDateOfConfinementMethod } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/EstimatedDateOfConfinementMethodEnum";
import { QuebecObstetricalFileFormPageThree } from "@/Patient/Models/ObstetricalFile/Quebec/PageThree/QuebecObstetricalFileFormPageThree";
import { Ultrasound } from "@/Patient/Models/ObstetricalFile/Quebec/PageThree/Ultrasound";
import { QuebecObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFileForm";
import { UltrasoundTable } from "./Subcomponent/UltrasoundTableComponent";

class QuebecObstetricalFilePageThreeState extends QuebecObstetricalFilePageState {
    public pageModel: QuebecObstetricalFileFormPageThree;
}

class QuebecObstetricalFilePageThree extends QuebecObstetricalFilePage<QuebecObstetricalFilePageThreeState> {
    private readonly componentId = "obstetrical-file-page-three";

    protected initState(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>): void {
        this.mutateState(s => {
            s.form = new QuebecObstetricalFileForm(props.form);
            s.pageModel = s.form.PageThree;

            if (this.props.obstetricalFileMedicalSummary.hasInfluenzaImmunization(
                this.props.encounter.ConsultationDate,
                this.props.consultationDateAtStartOfObstetricalFile
            ))
                s.pageModel.ComplementaryExaminations.HasVaccinationInfluenza = true;
        });
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId}>
                {this.renderComplementaryExaminations()}
                {this.renderPossibleDiscussionsSection()}
            </div>
        );
    }

    private renderComplementaryExaminations(): JSX.Element {
        const complementaryExaminations = this.state.pageModel.ComplementaryExaminations;
        const resultsId = "complementary-exams-results";

        if (!complementaryExaminations)
            return null;

        return (
            <div className="section border border-top section-complementary-exam">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("ObstetricalFile_ComplementaryExaminationsLabel")}</h4>
                    </div>
                </div>
                <div className="row complementary-examinations">
                    <h5 className="label inline">{cacheStore.GetString("HemoglobinAbbreviationLabel")}</h5>
                    <div className="field inline regular">
                        {this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.Hemoglobin0,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.Hemoglobin0 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsHemoglobin0.MaxChars
                        })}
                    </div>
                    <div className="field inline">
                        {this.controlRenderer.renderDateSelector({
                            value: complementaryExaminations.Hemoglobin0Date,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.Hemoglobin0Date = value),
                            id: "Hemoglobin0Date"
                        })}
                    </div>
                    <div className="field inline regular">
                        {this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.Hemoglobin1,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.Hemoglobin1 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsHemoglobin1.MaxChars
                        })}
                    </div>
                    <div className="field inline">
                        {this.controlRenderer.renderDateSelector({
                            value: complementaryExaminations.Hemoglobin1Date,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.Hemoglobin1Date = value),
                            id: "Hemoglobin1Date"
                        })}
                    </div>
                    <h5 className="label inline margin-left-auto">{cacheStore.GetString("ThyroidStimulationHormoneAbbreviationLabel")}</h5>
                    <div className="field inline regular">
                        {this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.ThyroidStimulationHormoneValue0,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ThyroidStimulationHormoneValue0 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsThyroidStimulationHormoneValue0.MaxChars
                        })}
                    </div>
                    <div className="field inline regular">
                        {this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.ThyroidStimulationHormoneValue1,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ThyroidStimulationHormoneValue1 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsThyroidStimulationHormoneValue1.MaxChars
                        })}
                    </div>
                    <div className="field inline regular">
                        {this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.ThyroidStimulationHormoneValue2,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ThyroidStimulationHormoneValue2 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsThyroidStimulationHormoneValue2.MaxChars
                        })}
                    </div>
                </div>
                <div className="row complementary-examinations">
                    <h5 className="label">{cacheStore.GetString("GlycemiaLabel")}</h5>
                    {this.renderField(
                        "ObstetricalFile_GlycemiaFasting",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.GlycemiaFasting,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.GlycemiaFasting = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsGlycemiaFasting.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "ObstetricalFile_GlycemiaPost50g",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.GlycemiaPost50g,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.GlycemiaPost50g = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsGlycemiaPost50g.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "ObstetricalFile_GlycemiaPost75g",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.GlycemiaPost75g,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.GlycemiaPost75g = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsGlycemiaPost75g.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.GlycemiaOpen0,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.GlycemiaOpen0 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsGlycemiaOpen0.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.GlycemiaOpen1,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.GlycemiaOpen1 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsGlycemiaOpen1.MaxChars
                        }),
                        "regular"
                    )}
                    <h5 className="label margin-left-auto">{cacheStore.GetString("UrineLabel")}</h5>
                    {this.renderField(
                        "",
                        this.controlRenderer.renderAutoCompleteTextbox({
                            value: complementaryExaminations.Urine,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(cacheStore.GetString("ObstetricalFile_PositiveCulture"), "PositiveCulture"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("ObstetricalFile_NegativeCulture"), "NegativeCulture"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("ObstetricalFile_PossibleContamination"), "PossibleContamination"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("ObstetricalFile_NormalAnalysis"), "NormalAnalysis"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("ObstetricalFile_AbnormalAnalysis"), "AbnormalAnalysis")
                                ],
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.Urine = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutateState(s => s.pageModel.ComplementaryExaminations.Urine = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsUrine.MaxChars,
                            disableMatchAutoSorting: true
                        }),
                        "auto urine"
                    )}
                </div>
                <div className="row complementary-examinations">
                    <h5 className="label">{cacheStore.GetString("ObstetricalFile_MotherTypeAndRhFactor")}</h5>
                    {this.renderField(
                        "",
                        this.controlRenderer.renderAutoCompleteTextbox({
                            value: complementaryExaminations.MothersTypeRhFactor?.RhType,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(cacheStore.GetString("A+"), "A+"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("A-"), "A-"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("B+"), "B+"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("B-"), "B-"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("AB+"), "AB+"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("AB-"), "AB-"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("O+"), "O+"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("O-"), "O-")
                                ],
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.MothersTypeRhFactor.RhType = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutateState(s => s.pageModel.ComplementaryExaminations.MothersTypeRhFactor.RhType = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsMothersTypeRhType.MaxChars,
                            disableMatchAutoSorting: true
                        })
                    )}
                    {this.renderField(
                        "AntibodiesLabel",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.MothersTypeRhFactor?.Antibodies0,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.MothersTypeRhFactor.Antibodies0 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsMothersTypeRhFactorAntibodies0.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.MothersTypeRhFactor?.Antibodies1,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.MothersTypeRhFactor.Antibodies1 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsMothersTypeRhFactorAntibodies1.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.MothersTypeRhFactor?.Antibodies2,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.MothersTypeRhFactor.Antibodies2 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsMothersTypeRhFactorAntibodies2.MaxChars
                        }),
                        "regular"
                    )}
                    <h5 className="label">{cacheStore.GetString("ObstetricalFile_WinRhoLabel")}</h5>
                    <div className="field">
                        {this.renderField("", this.controlRenderer.renderDateSelector({
                            value: complementaryExaminations.WinrhoDate0,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.WinrhoDate0 = value),
                            id: "WinrhoDate0"
                        }))}
                    </div>
                    <div className="field">
                        {this.renderField(
                            "",
                            this.controlRenderer.renderDateSelector({
                                value: complementaryExaminations.WinrhoDate1,
                                onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.WinrhoDate1 = value),
                                id: "WinrhoDate1"
                            })
                        )}
                    </div>
                </div>
                <div className="row complementary-examinations ">
                    <h5 className="label">{cacheStore.GetString("ObstetricalFile_STBBIScreeningLabel")}</h5>
                    {this.renderRadioButtonField(
                        "GonorrheaLabel",
                        this.controlRenderer.renderPositiveNegativeCheckBoxes(
                            complementaryExaminations.StbbiScreening?.HasGonorrhea,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.StbbiScreening.HasGonorrhea = value),
                            "Gonorrhea"
                        ),
                        "inline"
                    )}
                    {this.renderRadioButtonField(
                        "ChlamydiaLabel",
                        this.controlRenderer.renderPositiveNegativeCheckBoxes(
                            complementaryExaminations.StbbiScreening?.HasChlamydia,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.StbbiScreening.HasChlamydia = value),
                            "Chlamydia"
                        ),
                        "inline"
                    )}
                    {this.renderRadioButtonField(
                        "HepatitisBSurfaceAntigenAbbrevLabel",
                        this.controlRenderer.renderPositiveNegativeCheckBoxes(
                            complementaryExaminations.StbbiScreening?.HasHBsAg,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.StbbiScreening.HasHBsAg = value),
                            "HBsAg"
                        ),
                        "inline"
                    )}
                    {this.renderRadioButtonField(
                        "SyphilisLabel",
                        this.controlRenderer.renderPositiveNegativeCheckBoxes(
                            complementaryExaminations.StbbiScreening?.HasSyphilis,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.StbbiScreening.HasSyphilis = value),
                            "Syphillis"
                        ),
                        "inline"
                    )}
                    {this.renderRadioButtonField(
                        "HumanImmunodeficiencyVirusAbbreviationLabel",
                        this.controlRenderer.renderPositiveNegativeCheckBoxes(
                            complementaryExaminations.StbbiScreening?.HasHiv,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.StbbiScreening.HasHiv = value),
                            "HIV"
                        ),
                        "inline"
                    )}
                </div>
                <div className="row complementary-examinations">
                    <h5 className="label">{cacheStore.GetString("ObstetricalFile_ImmunitySerologyLabel")}</h5>
                    {this.renderRadioButtonField(
                        "Rubella",
                        this.controlRenderer.renderPositiveNegativeCheckBoxes(
                            complementaryExaminations.ImmunitySerology?.HasRubella,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ImmunitySerology.HasRubella = value),
                            "Rubella"
                        )
                    )}
                    {this.renderField(
                        "ParvoB19Label",
                        this.controlRenderer.renderTextboxWithPositiveNegativeOption({
                            value: complementaryExaminations.ImmunitySerology?.ParvoB19,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ImmunitySerology.ParvoB19 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsImmunitySerologyParvoB19.MaxChars
                        })
                    )}
                    {this.renderField(
                        "ChickenPoxLabel",
                        this.controlRenderer.renderTextboxWithPositiveNegativeOption({
                            value: complementaryExaminations.ImmunitySerology?.ChickenPox,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ImmunitySerology.ChickenPox = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsImmunitySerologyChickenPox.MaxChars
                        })
                    )}
                    {this.renderField(
                        "ToxoLabel",
                        this.controlRenderer.renderTextboxWithPositiveNegativeOption({
                            value: complementaryExaminations.ImmunitySerology?.Toxo,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ImmunitySerology.Toxo = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsImmunitySerologyToxo.MaxChars
                        })
                    )}
                    {this.renderField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.ImmunitySerology?.Value0,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ImmunitySerology.Value0 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsImmunitySerologyValue0.MaxChars
                        }),
                        "regular"
                    )}
                    {this.renderField(
                        "",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.ImmunitySerology?.Value1,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.ImmunitySerology.Value1 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsImmunitySerologyValue1.MaxChars
                        }),
                        "regular"
                    )}
                </div>
                <div className="row complementary-examinations">
                    <h5 className="label">{cacheStore.GetString("ObstetricalFile_T21ScreeningLabel")}</h5>
                    {this.renderField(
                        "NuchalTranslucencyLabel",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.T21ScreeningNuchalTranslucency,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.T21ScreeningNuchalTranslucency = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsT21ScreeningNuchalTranslucency.MaxChars
                        }),
                        "auto regular"
                    )}
                    <h5 className="label margin-left-auto">{cacheStore.GetString("ObstetricalFile_RiskAccordingToLabel")}</h5>
                    {this.renderField(
                        "ObstetricalFile_QPSPT21Label",
                        this.controlRenderer.renderAutoCompleteTextbox({
                            value: complementaryExaminations.RiskAccordingToQpspt21,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(cacheStore.GetString("High"), "High"),
                                    new SearchableItemModelT<string>(cacheStore.GetString("Low"), "Low")
                                ],
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.RiskAccordingToQpspt21 = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutateState(s => s.pageModel.ComplementaryExaminations.RiskAccordingToQpspt21 = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsRiskAccordingToQpspt21.MaxChars,
                            disableMatchAutoSorting: true
                        })
                    )}
                    {this.renderField(
                        "ObstetricalFile_OtherLabel",
                        this.controlRenderer.renderTextbox({
                            value: complementaryExaminations.RiskAccordingToOther,
                            onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.RiskAccordingToOther = value),
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsRiskAccordingToOther.MaxChars
                        }),
                        "auto risk-other regular"
                    )}
                </div>
                <div className="row complementary-examinations ultrasound">
                    <UltrasoundTable
                        ultrasounds={this.state.pageModel.ComplementaryExaminations.Ultrasounds}
                        readonly={this.props.readonly}
                        onChange={(ultrasounds, shouldHandleFormUpdate, estimatedDateOfConfinementCalculationChanged) => this.onUltrasoundRowChange(ultrasounds, shouldHandleFormUpdate, estimatedDateOfConfinementCalculationChanged)}
                        controlRenderer={this.controlRenderer}
                    />
                    <div className="flex-column">
                        {this.renderRadioButtonField(
                            "ObstetricalFile_Amniocentesis",
                            this.controlRenderer.renderYesNoRadio(
                                complementaryExaminations.IsAmniocentesis,
                                value => this.mutateState(s => s.pageModel.ComplementaryExaminations.IsAmniocentesis = value),
                                "isAmniocentesis"
                            ),
                            "amniocentesis"
                        )}
                        <div className="field auto results">
                            <span className="field-label">{cacheStore.GetString("ResultsLabel")}</span>
                            {this.controlRenderer.renderTextarea({
                                value: complementaryExaminations.Results,
                                onChange: value => this.mutateState(s => s.pageModel.ComplementaryExaminations.Results = value),
                                id: resultsId,
                                maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsAmniocentesisResults.MaxChars,
                                maxRows: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsAmniocentesisResults.MaxRows,
                                clickableTextOptions: this.getClickableTextOptions(resultsId, QuebecObstetricalFileClickableTextContext.PageThree_AmniocentesisResults)
                            })}
                        </div>
                    </div>
                </div>
                <div className="row complementary-examinations">
                    <h5 className="label">{cacheStore.GetString("VaccinationLabel")}</h5>
                    {this.renderRadioButtonField(
                        "ObstetricalFile_PostPartumRubella",
                        this.controlRenderer.renderYesNoRadio(
                            complementaryExaminations.HasVaccinationPostPartumRubella,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.HasVaccinationPostPartumRubella = value),
                            "ppRubella"
                        ),
                        "inline"
                    )}
                    {this.renderRadioButtonField(
                        "ObstetricalFile_PostPartumChickenPox",
                        this.controlRenderer.renderYesNoRadio(
                            complementaryExaminations.HasVaccinationPostPartumChickenPox,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.HasVaccinationPostPartumChickenPox = value),
                            "ppChickenPox"
                        ),
                        "inline"
                    )}
                    {this.renderRadioButtonField(
                        "InfluenzaLabel",
                        this.controlRenderer.renderYesNoRadio(
                            complementaryExaminations.HasVaccinationInfluenza,
                            value => this.mutateState(s => s.pageModel.ComplementaryExaminations.HasVaccinationInfluenza = value),
                            "influenza"
                        ),
                        "inline"
                    )}
                </div>
            </div>
        );
    }

    private renderPossibleDiscussionsSection(): JSX.Element {
        const commentsId = "possible-discussions-comments";
        if (!this.state.pageModel?.PossibleDiscussions)
            return null;

        return (
            <div className="section section-possible-discussions border border-top">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("ObstetricalFile_PossibleDiscussionsLabel")}</h4>
                    </div>
                </div>

                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_GeneticAdviceLabel",
                        this.state.pageModel.PossibleDiscussions.HasGeneticAdvice,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasGeneticAdvice = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_HospitalRoutineLabel",
                        this.state.pageModel.PossibleDiscussions.HasHospitalRoutine,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasHospitalRoutine = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_AnesthesiaPainReliefLabel",
                        this.state.pageModel.PossibleDiscussions.HasAnesthesiaPainRelief,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasAnesthesiaPainRelief = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_HospitalStayLabel",
                        this.state.pageModel.PossibleDiscussions.HasHospitalStay,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasHospitalStay = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_TravelLabel",
                        this.state.pageModel.PossibleDiscussions.HasTravel,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasTravel = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_BirthingCoachLabel",
                        this.state.pageModel.PossibleDiscussions.HasBirthingCoach,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasBirthingCoach = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_VacuumExtractorForcepsLabel",
                        this.state.pageModel.PossibleDiscussions.HasVacuumExtractorForceps,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasVacuumExtractorForceps = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_CohabitationLabel",
                        this.state.pageModel.PossibleDiscussions.HasCohabitation,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasCohabitation = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_SexualActivityLabel",
                        this.state.pageModel.PossibleDiscussions.HasSexualActivity,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasSexualActivity = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_BirthingRoomLabel",
                        this.state.pageModel.PossibleDiscussions.HasBirthingRoom,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasBirthingRoom = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_EpisiotomyLabel",
                        this.state.pageModel.PossibleDiscussions.HasEpisiotomy,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasEpisiotomy = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_NursingLabel",
                        this.state.pageModel.PossibleDiscussions.HasNursing,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasNursing = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_ExerciseSportsLabel",
                        this.state.pageModel.PossibleDiscussions.HasExerciseSports,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasExerciseSports = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_SupervisonDuringLabourLabel",
                        this.state.pageModel.PossibleDiscussions.HasSupervisionDuringLabour,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasSupervisionDuringLabour = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_PerinealMassageLabel",
                        this.state.pageModel.PossibleDiscussions.HasPerinealMassage,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasPerinealMassage = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_CircumcisionLabel",
                        this.state.pageModel.PossibleDiscussions.HasCircumcision,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasCircumcision = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_PrenatalClassesLabel",
                        this.state.pageModel.PossibleDiscussions.HasPrenatalClasses,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasPrenatalClasses = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_MonitoringLabel",
                        this.state.pageModel.PossibleDiscussions.HasMonitoring,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasMonitoring = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_CaesarianBirthLabel",
                        this.state.pageModel.PossibleDiscussions.HasCaesarianBirth,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasCaesarianBirth = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_ContraceptionLabel",
                        this.state.pageModel.PossibleDiscussions.HasContraception,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasContraception = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_MedicalAvailabilityLabel",
                        this.state.pageModel.PossibleDiscussions.HasMedicalAvailability,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasMedicalAvailability = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_WorriesFearLabel",
                        this.state.pageModel.PossibleDiscussions.HasWorriesFear,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasWorriesFear = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_DeliveryPositionTechniquesLabel",
                        this.state.pageModel.PossibleDiscussions.HasDeliveryPositionTechniques,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasDeliveryPositionTechniques = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_FinancialResourcesLabel",
                        this.state.pageModel.PossibleDiscussions.HasFinancialResources,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasFinancialResources = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_NutritionLabel",
                        this.state.pageModel.PossibleDiscussions.HasNutrition,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasNutrition = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_STBBIRisksLabel",
                        this.state.pageModel.PossibleDiscussions.HasStbbiRisks,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasStbbiRisks = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_NeonatalBloodUrineScreeningLabel",
                        this.state.pageModel.PossibleDiscussions.HasNeonatalBloodUrineScreening,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasNeonatalBloodUrineScreening = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_ResourcesAtHomeLabel",
                        this.state.pageModel.PossibleDiscussions.HasResourcesAtHome,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasResourcesAtHome = value, () => this.handleFormUpdate()),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.controlRenderer.renderCheckbox(
                        "ObstetricalFile_ConjugalViolenceLabel",
                        this.state.pageModel.PossibleDiscussions.HasConjugalViolence,
                        value => this.mutateState(s => s.pageModel.PossibleDiscussions.HasConjugalViolence = value, () => this.handleFormUpdate()),
                        "col-xs-12"
                    )}
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("CommentsLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: this.state.pageModel.PossibleDiscussions.Comments,
                            onChange: value => this.mutateState(s => s.pageModel.PossibleDiscussions.Comments = value),
                            id: commentsId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page3.PossibleDiscussionsComments.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page3.PossibleDiscussionsComments.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(commentsId, QuebecObstetricalFileClickableTextContext.PageThree_PossibleDiscussions)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private onUltrasoundRowChange(ultrasounds: Ultrasound[], shouldHandleFormUpdate: boolean, estimatedDateOfConfinementCalculationChanged: boolean): void {
        this.mutateState(
            s => {
                s.pageModel.ComplementaryExaminations.Ultrasounds = ultrasounds;

                const ultrasoundUsedForDating = ultrasounds?.firstOrDefault(p => p.UseForDating);
                if (!ultrasoundUsedForDating || !estimatedDateOfConfinementCalculationChanged)
                    return;

                if (ultrasoundUsedForDating.UltrasoundAge?.NumberOfWeeksNumber < 20) {
                    s.form.PageFour.EstimatedDateOfConfinementUltrasound20WeeksDate = ObstetricsCalculator.calculateDateOfBirthBasedOnUltrasound(ultrasoundUsedForDating.Date, ultrasoundUsedForDating.UltrasoundAge.TotalNumberOfDays);
                    s.form.PageFour.EstimatedDateOfConfinementMethod = EstimatedDateOfConfinementMethod.Ultrasound20WeeksDate;
                }
                else {
                    s.form.PageFour.EstimatedDateOfConfinementOtherUltrasound = ObstetricsCalculator.calculateDateOfBirthBasedOnUltrasound(ultrasoundUsedForDating.Date, ultrasoundUsedForDating.UltrasoundAge?.TotalNumberOfDays);
                    s.form.PageFour.EstimatedDateOfConfinementMethod = EstimatedDateOfConfinementMethod.OtherUltrasound;
                }

                s.form.PageFour.updateWeeksOfGestation(this.props.contributionId);
            },
            () => shouldHandleFormUpdate && this.handleFormUpdate?.()
        );
    }
}

export { QuebecObstetricalFilePageThree };
